import { getDocs, doc, writeBatch } from "firebase/firestore";
import { getBlob, getMetadata, ref } from "firebase/storage";

import {
  SalesPagesContentRef,
  SalesPagesContentPublishedRef,
  db,
  storage,
} from "../../../../firebase";
import {
  SALES_COLUMN_FIELDS_BY_TYPE,
  SALES_SECTION_REGISTRY,
} from "../../../../constants";
import {
  DEFAULT_IMAGE_SIZE,
  IMAGE_SIZE_PRESETS,
  isOptimizableImage,
  largerImageSize,
  optimizeImageFile,
} from "../../../../utils/imageOptimization";
import { uploadPublicFile, uploadSalesImage } from "./SalesPagesHook";

/**
 * One-off optimizer for images already used on quicktales.com.
 *
 * Scope: Storage objects under `salesPagesContent/` and `uploads/` that are
 * referenced from draft or published section docs. Originals are NOT deleted.
 * References are swapped in both collections WITHOUT touching `updatedAt`, so
 * publish-pending status is unchanged.
 */

const MIN_BYTES = 100 * 1024;
const ALLOWED_PREFIXES = ["salesPagesContent/", "uploads/"];

// Firebase download URLs; stops at quotes, whitespace, parens, < and >.
const STORAGE_URL_RE =
  /https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/\s"'<>()]+\/o\/[^\s"'<>()]+/g;

export const storagePathFromUrl = (url) => {
  const m = url.match(/\/o\/([^?#]+)/);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch (e) {
    return null;
  }
};

const isInScope = (path) =>
  !!path && ALLOWED_PREFIXES.some((p) => path.startsWith(p));

const fieldSizeMap = (fields = []) =>
  Object.fromEntries(
    fields
      .filter((f) => f.type === "image")
      .map((f) => [f.key, f.imageSize || DEFAULT_IMAGE_SIZE])
  );

// Preset for a string leaf based on where it sits inside `content`.
const presetForPath = (sectionType, pathKeys) => {
  const def = SALES_SECTION_REGISTRY[sectionType] || {};
  const key = pathKeys[pathKeys.length - 1];
  if (pathKeys[0] === "items" && pathKeys.length === 3) {
    return fieldSizeMap(def.itemFields)[key];
  }
  if (pathKeys.length === 1) return fieldSizeMap(def.fields)[key];
  if (pathKeys.length === 2) {
    // twoColumn: content.left / content.right column objects.
    const isColumn = (def.fields || []).some(
      (f) => f.key === pathKeys[0] && f.type === "column"
    );
    if (isColumn) {
      return fieldSizeMap(Object.values(SALES_COLUMN_FIELDS_BY_TYPE).flat())[key];
    }
  }
  return undefined;
};

// Visit every string in a value: visit(string, pathKeys).
const walk = (value, pathKeys, visit) => {
  if (typeof value === "string") {
    visit(value, pathKeys);
  } else if (Array.isArray(value)) {
    value.forEach((v, i) => walk(v, [...pathKeys, i], visit));
  } else if (value && typeof value === "object") {
    Object.entries(value).forEach(([k, v]) => walk(v, [...pathKeys, k], visit));
  }
};

const replaceDeep = (value, map) => {
  if (typeof value === "string") {
    let out = value;
    map.forEach((newUrl, oldUrl) => {
      if (out.includes(oldUrl)) out = out.split(oldUrl).join(newUrl);
    });
    return out;
  }
  if (Array.isArray(value)) return value.map((v) => replaceDeep(v, map));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, replaceDeep(v, map)])
    );
  }
  return value;
};

const loadSectionDocs = async () => {
  const [draft, published] = await Promise.all([
    getDocs(SalesPagesContentRef),
    getDocs(SalesPagesContentPublishedRef),
  ]);
  const toList = (snap, collectionName) =>
    snap.docs.map((d) => ({ id: d.id, collectionName, ...d.data() }));
  return [
    ...toList(draft, "salesPagesContent"),
    ...toList(published, "salesPagesContentPublished"),
  ];
};

/**
 * Dry run. Returns { docs, candidates } where each candidate is
 * { path, urls:Set, page, preset, size, contentType, action, reason }.
 * action is "optimize" or "skip".
 */
export const scanSalesImages = async () => {
  const docs = await loadSectionDocs();
  const byPath = new Map();

  docs.forEach((d) => {
    walk(d.content, [], (str, pathKeys) => {
      (str.match(STORAGE_URL_RE) || []).forEach((url) => {
        const path = storagePathFromUrl(url);
        if (!isInScope(path)) return;
        // Field preset applies only when the whole string is the URL;
        // URLs embedded in rich text / HTML fall back to the default (large).
        const preset =
          (str.trim() === url && presetForPath(d.section, pathKeys)) ||
          DEFAULT_IMAGE_SIZE;
        const entry = byPath.get(path) || {
          path,
          urls: new Set(),
          preset: null,
          page: d.page,
        };
        entry.urls.add(url);
        entry.preset = entry.preset ? largerImageSize(entry.preset, preset) : preset;
        byPath.set(path, entry);
      });
    });
  });

  const candidates = await Promise.all(
    Array.from(byPath.values()).map(async (entry) => {
      try {
        const meta = await getMetadata(ref(storage, entry.path));
        const base = {
          ...entry,
          size: Number(meta.size) || 0,
          contentType: meta.contentType || "",
          action: "optimize",
          reason: "",
        };
        const skip = (reason) => ({ ...base, action: "skip", reason });
        if (!isOptimizableImage({ name: entry.path, type: base.contentType })) {
          return skip("GIF/SVG/ICO or non-image");
        }
        if (meta.customMetadata?.imageSize) return skip("Already optimized");
        if (base.size < MIN_BYTES) return skip("Under 100 KB");
        return base;
      } catch (error) {
        return { ...entry, size: 0, action: "skip", reason: "Missing in Storage" };
      }
    })
  );

  candidates.sort((a, b) => b.size - a.size);
  return { docs, candidates };
};

/**
 * Optimize the "optimize" candidates, upload new copies, and rewrite all
 * references. onProgress({ done, total, current }) reports progress.
 * Returns { results, updatedDocs }.
 */
export const runSalesImageOptimization = async (
  { docs, candidates },
  onProgress
) => {
  const todo = candidates.filter((c) => c.action === "optimize");
  const urlMap = new Map();
  const results = [];

  for (let i = 0; i < todo.length; i += 1) {
    const c = todo[i];
    onProgress?.({ done: i, total: todo.length, current: c.path });
    try {
      const blob = await getBlob(ref(storage, c.path));
      const name = c.path
        .split("/")
        .pop()
        .replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, "");
      const file = new File([blob], name, { type: c.contentType || blob.type });
      const res = await optimizeImageFile(file, {
        maxDimension: IMAGE_SIZE_PRESETS[c.preset].maxDimension,
        minBytes: MIN_BYTES,
      });
      if (!res.optimized) {
        results.push({ ...c, status: "unchanged", reason: res.reason });
        continue;
      }
      const meta = { imageSize: c.preset, originalName: name, replaces: c.path };
      const newUrl = c.path.startsWith("uploads/")
        ? await uploadPublicFile(res.file, meta)
        : await uploadSalesImage(res.file, c.path.split("/")[1] || c.page, meta);
      c.urls.forEach((u) => urlMap.set(u, newUrl));
      results.push({ ...c, status: "optimized", newSize: res.newSize, newUrl });
    } catch (error) {
      console.log("Error optimizing", c.path, error.message);
      results.push({ ...c, status: "error", reason: error.message });
    }
  }
  onProgress?.({ done: todo.length, total: todo.length, current: "" });

  // Only rewrite docs whose content changes; leave updatedAt alone.
  const changed = docs
    .map((d) => ({ d, next: replaceDeep(d.content, urlMap) }))
    .filter(({ d, next }) => JSON.stringify(next) !== JSON.stringify(d.content));

  for (let i = 0; i < changed.length; i += 400) {
    const batch = writeBatch(db);
    changed.slice(i, i + 400).forEach(({ d, next }) => {
      batch.update(doc(db, d.collectionName, d.id), { content: next });
    });
    await batch.commit();
  }

  return { results, updatedDocs: changed.length };
};

